import { DestroyRef, Injectable, InjectionToken, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventType, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { ConnectionSettings, CopilotStudioClient, PowerPlatformCloud, ScopeHelper } from '@microsoft/agents-copilotstudio-client';
import { _, TranslateService } from '@ngx-translate/core';
import { ChatAgentConfiguration } from '../../../../environments/env-def';
import { environment } from '../../../../environments/environment';
import { ADAPTIVE_CARD_CONTENT_TYPE, ChatAction, parseAdaptiveCard } from './adaptive-card';

// Derived from the client so the Activity package is not a direct dependency (it broke production bundles).
type Activity = Parameters<CopilotStudioClient['executeStreaming']>[0];

_(['Chat.ConfigurationError', 'Chat.SignInRequired', 'Chat.ConnectionError', 'Chat.SendError', 'Chat.UnsupportedContent', 'Chat.ConversationExpired', 'Chat.NoResponse']);

export interface ChatMessage {
  id: string;
  role: 'user' | 'agent';
  text: string;
  suggestions: string[];
  /** Buttons offered with an agent message; removed once one has been used. */
  actions: ChatAction[];
}

interface ChatTurn {
  activity: Record<string, unknown>;
  /** Text shown as the user's message; set only for text typed in the composer. */
  echo?: string;
  /** Starts a new conversation first, keeping the visible transcript and dropping the new greeting. */
  restart?: boolean;
  /** False when the agent may legitimately answer with nothing, such as a plain button submit. */
  expectReply?: boolean;
}

export type ChatClient = Pick<CopilotStudioClient, 'startConversationWithResponse' | 'executeStreaming'>;

export const CHAT_AGENT_CONFIGURATION = new InjectionToken<ChatAgentConfiguration>('CHAT_AGENT_CONFIGURATION', {
  providedIn: 'root',
  factory: () => environment.chatAgent,
});

export const CHAT_CLIENT_FACTORY = new InjectionToken<(settings: ConnectionSettings, token: string) => ChatClient>('CHAT_CLIENT_FACTORY', {
  providedIn: 'root',
  factory: () => (settings, token) => new CopilotStudioClient(settings, token),
});

// Evaluated at runtime on purpose: the minifier would fold a build-time comparison of the token string before it is replaced.
export function isChatEnabled(configuration: ChatAgentConfiguration): boolean {
  const enabled = configuration?.enabled;
  return enabled === true || (typeof enabled === 'string' && enabled.trim().toLowerCase() === 'true');
}

export function resolveChatSettings(configuration: ChatAgentConfiguration): ConnectionSettings | null {
  if (!isChatEnabled(configuration)) {
    return null;
  }
  const environmentId = configuration.environmentId?.trim() ?? '';
  const schemaName = configuration.agentIdentifier?.trim() ?? '';
  if (!/^(Default-)?[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(environmentId) || !/^[\w-]+$/.test(schemaName)) {
    return null;
  }
  return new ConnectionSettings({ environmentId, schemaName, cloud: PowerPlatformCloud.Prod });
}

@Injectable({ providedIn: 'root' })
export class CopilotChatService {
  readonly messages = signal<ChatMessage[]>([]);
  readonly busy = signal(false);
  readonly connected = signal(false);
  readonly error = signal<string | null>(null);
  readonly signInRequired = signal(false);
  readonly draft = signal('');
  readonly signedIn = signal(false);
  readonly enabled = computed(() => isChatEnabled(this.configuration));
  readonly configured = computed(() => resolveChatSettings(this.configuration) !== null);
  readonly canSend = computed(() => this.signedIn() && this.configured() && !this.busy() && !this.signInRequired());

  private readonly configuration = inject(CHAT_AGENT_CONFIGURATION);
  private readonly createClient = inject(CHAT_CLIENT_FACTORY);
  private readonly msal = inject(MsalService);
  private readonly broadcast = inject(MsalBroadcastService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private conversationId = '';
  private accountId = '';
  private generation = 0;
  private interactionStatus: InteractionStatus = InteractionStatus.Startup;
  private activeStream: AsyncGenerator<Activity> | null = null;
  private lastUserText = '';
  private repliesInTurn = 0;
  private readonly streamMessages = new Map<string, string>();

  constructor() {
    this.broadcast.inProgress$.pipe(takeUntilDestroyed()).subscribe(status => {
      this.interactionStatus = status;
      if (status === InteractionStatus.None) {
        this.syncAccount();
      }
    });
    this.broadcast.msalSubject$.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event.eventType === EventType.LOGOUT_START || event.eventType === EventType.ACCOUNT_REMOVED) {
        this.reset();
        this.signedIn.set(false);
      }
      if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACTIVE_ACCOUNT_CHANGED) {
        this.syncAccount();
      }
    });
    this.destroyRef.onDestroy(() => this.reset());
  }

  async connect(): Promise<void> {
    if (this.connected() || this.busy()) {
      return;
    }
    await this.runTurn();
  }

  async send(text: string): Promise<boolean> {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > 8000 || !this.canSend()) {
      return false;
    }
    return this.runTurn({ activity: { type: 'message', text: trimmed, locale: this.translate.getCurrentLang() || 'en' }, echo: trimmed });
  }

  /** Answers a button offered with an agent message; each set of buttons can only be used once. */
  async invokeAction(messageId: string, action: ChatAction): Promise<boolean> {
    if (!this.canSend()) {
      return false;
    }
    this.messages.update(messages => messages.map(message => (message.id === messageId ? { ...message, actions: [] } : message)));
    if (action.kind === 'dismiss') {
      return true;
    }
    // The connection card's Retry needs a new conversation: the old one keeps the connection state it had when the tool first asked to connect.
    const retryText = action.kind === 'retry' ? this.lastUserText : '';
    this.messages.update(messages => [...messages, { id: crypto.randomUUID(), role: 'user', text: action.label, suggestions: [], actions: [] }]);
    if (retryText) {
      return this.runTurn({ activity: { type: 'message', text: retryText, locale: this.translate.getCurrentLang() || 'en' }, restart: true });
    }
    return this.runTurn({ activity: { type: 'message', value: action.payload }, expectReply: false });
  }

  async newConversation(): Promise<void> {
    this.reset();
    this.syncAccount();
    await this.connect();
  }

  reset(): void {
    this.generation++;
    const stream = this.activeStream;
    this.activeStream = null;
    if (stream) {
      void stream.return(undefined).catch(() => undefined);
    }
    this.conversationId = '';
    this.accountId = '';
    this.lastUserText = '';
    this.streamMessages.clear();
    this.messages.set([]);
    this.draft.set('');
    this.busy.set(false);
    this.connected.set(false);
    this.error.set(null);
    this.signInRequired.set(false);
  }

  async signIn(): Promise<void> {
    const settings = resolveChatSettings(this.configuration);
    const account = this.msal.instance.getActiveAccount();
    if (!settings || !account || this.interactionStatus !== InteractionStatus.None) {
      return;
    }
    try {
      await this.msal.instance.acquireTokenRedirect({ account, scopes: [ScopeHelper.getScopeFromSettings(settings)] });
    } catch {
      this.error.set('Chat.ConnectionError');
    }
  }

  private syncAccount(): void {
    const account = this.msal.instance.getActiveAccount();
    const accountId = account ? `${account.homeAccountId}:${account.tenantId}` : '';
    if (this.accountId && this.accountId !== accountId) {
      this.reset();
    }
    this.accountId = accountId;
    this.signedIn.set(!!account);
  }

  private async runTurn(turn?: ChatTurn): Promise<boolean> {
    this.syncAccount();
    const settings = resolveChatSettings(this.configuration);
    if (!settings) {
      this.error.set('Chat.ConfigurationError');
      return false;
    }
    if (this.interactionStatus !== InteractionStatus.None || !this.signedIn()) {
      this.signInRequired.set(true);
      this.error.set('Chat.SignInRequired');
      return false;
    }
    const generation = this.generation;
    const account = this.msal.instance.getActiveAccount();
    this.busy.set(true);
    this.error.set(null);
    this.signInRequired.set(false);
    let submitted = false;
    try {
      const token = await this.msal.instance.acquireTokenSilent({ account, scopes: [ScopeHelper.getScopeFromSettings(settings)] });
      if (!this.isCurrent(generation)) {
        return false;
      }
      const client = this.createClient(settings, token.accessToken);
      if (turn?.restart) {
        this.conversationId = '';
      }
      if (!this.conversationId) {
        const response = await client.startConversationWithResponse({ locale: this.translate.getCurrentLang() || 'en', emitStartConversationEvent: true });
        if (!this.isCurrent(generation)) {
          return false;
        }
        this.conversationId = response.conversationId;
        if (!this.conversationId) {
          throw new Error('Missing conversation ID');
        }
        if (!turn?.restart) {
          response.activities.forEach(activity => this.receive(activity));
        }
        this.connected.set(true);
      }
      if (turn) {
        this.streamMessages.clear();
        this.repliesInTurn = 0;
        const echo = turn.echo;
        if (echo) {
          this.lastUserText = echo;
          this.messages.update(messages => [...messages, { id: crypto.randomUUID(), role: 'user', text: echo, suggestions: [], actions: [] }]);
          this.draft.set('');
        }
        submitted = true;
        // A plain object is enough: the SDK only reads type and serializes the activity as JSON. The Activity class failed in production bundles.
        const stream = client.executeStreaming(turn.activity as unknown as Activity, this.conversationId);
        this.activeStream = stream;
        try {
          for await (const activity of stream) {
            if (!this.isCurrent(generation)) {
              break;
            }
            this.receive(activity);
          }
        } finally {
          if (this.activeStream === stream) {
            this.activeStream = null;
          }
        }
        if (this.isCurrent(generation) && turn.expectReply !== false && this.repliesInTurn === 0) {
          this.error.set('Chat.NoResponse');
        }
      }
      return this.isCurrent(generation);
    } catch (error) {
      if (this.isCurrent(generation)) {
        if (error instanceof InteractionRequiredAuthError) {
          this.signInRequired.set(true);
          this.error.set('Chat.SignInRequired');
        } else if (error instanceof Error && /\b(404|410)\b/.test(error.message)) {
          this.connected.set(false);
          this.error.set('Chat.ConversationExpired');
        } else {
          this.error.set(submitted ? 'Chat.SendError' : 'Chat.ConnectionError');
        }
      }
      return false;
    } finally {
      if (this.isCurrent(generation)) {
        this.busy.set(false);
      }
    }
  }

  private isCurrent(generation: number): boolean {
    const account = this.msal.instance.getActiveAccount();
    const currentAccountId = account ? `${account.homeAccountId}:${account.tenantId}` : '';
    if (generation !== this.generation) {
      return false;
    }
    if (this.accountId !== currentAccountId) {
      this.reset();
      this.signedIn.set(!!account);
      this.accountId = currentAccountId;
      return false;
    }
    return true;
  }

  private receive(activity: Activity): void {
    const attachments = activity.attachments ?? [];
    if (attachments.some(attachment => attachment.contentType !== ADAPTIVE_CARD_CONTENT_TYPE)) {
      this.error.set('Chat.UnsupportedContent');
    }
    if (activity.type !== 'message' && activity.type !== 'typing') {
      return;
    }
    const streamInfo = activity.entities?.find(entity => entity.type === 'streaminfo');
    const streamId = streamInfo?.['streamId'] ?? activity.channelData?.['streamId'];
    if (activity.type === 'typing' && !streamId) {
      return;
    }
    // Adaptive cards (for example the connection manager card) are reduced to their text and submit buttons.
    const cards = attachments.filter(attachment => attachment.contentType === ADAPTIVE_CARD_CONTENT_TYPE).map(attachment => parseAdaptiveCard(attachment.content));
    const text = [activity.text, ...cards.map(card => card.text)].filter(Boolean).join('\n\n');
    const actions = cards.flatMap(card => card.actions);
    const suggestions = (activity.suggestedActions?.actions ?? []).filter(action => action.type === 'imBack' && typeof action.value === 'string').map(action => action.value as string);
    if (!text && !suggestions.length && !actions.length) {
      return;
    }
    this.repliesInTurn++;
    const activityId = activity.id || crypto.randomUUID();
    const id = typeof streamId === 'string' ? this.streamMessages.get(streamId) ?? activityId : activityId;
    if (typeof streamId === 'string') {
      this.streamMessages.set(streamId, id);
    }
    this.messages.update(messages => {
      const previous = messages.find(message => message.id === id);
      const message: ChatMessage = { id, role: 'agent', text: text || previous?.text || '', suggestions, actions };
      return previous ? messages.map(existing => (existing.id === id ? message : existing)) : [...messages, message];
    });
  }
}
